import React, { useMemo, useCallback } from 'react';
import FilterBar from '../components/FilterBar/FilterBar';
import useOptionsMap from '../hooks/useOptionsMap';
import { withAll } from '../utils/options';

export default function FilterContainer({
    filters = [],
    state,
    setFilter,
    searchBar = true,
    setSearch,
}) {
    const optionDescriptors = useMemo(() => (
        (filters || [])
            .filter(f => f.type === 'select')
            .map(f => ({ key: f.key, options: f.options, loadOptions: f.loadOptions, allValue: f.allValue ?? 'All' }))
    ), [filters]);

    // stable augment to avoid retriggers
    const augment = useCallback(
        (f, base) => withAll(base, f.allValue ?? 'All', 'All'),
        []
    );
    const optionsMap = useOptionsMap(optionDescriptors, { augment });

    // --- helpers: read/write for flat OR nested state, and both setter signatures ---
    const readValue = useCallback(
        (k) => (state && Object.prototype.hasOwnProperty.call(state, k))
            ? state[k]
            : state?.filters?.[k],
        [state]
    );

    const writeValue = useCallback((k, v) => {
        if (typeof setFilter !== 'function') return;
        if (setFilter.length >= 2) return setFilter(k, v); // setFilter(key, value)
        return setFilter({ [k]: v }); // setFilter({ key: value })
    }, [setFilter]);

    // build filter config objects for the bar; pass through Field-compatible props
    const filtersForBar = useMemo(() => (
        (filters || []).map((f) => {
            // Special handling: daterange maps to two *param* keys
            if (f.type === 'daterange') {
                const startKey = f.query?.start || `${f.key}dateFrom`;
                const endKey = f.query?.end || `${f.key}dateTo`;
                const start = readValue(startKey) || '';
                const end = readValue(endKey) || '';
                return {
                    ...f,
                    key: f.key,
                    label: f.label,
                    value: { start, end },
                    onChange: undefined,
                    onMultiChange: ({ start: s, end: e }) => {
                        writeValue(startKey, s ?? '');
                        writeValue(endKey, e ?? '');
                    },
                    options: undefined,
                };
            }
            return {
                ...f,
                key: f.key,
                label: f.label,
                value: readValue(f.key),
                onChange: (v) => writeValue(f.key, v),
                options: optionsMap[f.key] || f.options,
            };
        })
    ), [filters, optionsMap, readValue, writeValue]);

    // If searchBar is false, do not render any search input or button
    if (!searchBar) {
        return <FilterBar filters={filtersForBar} search={undefined} prefix='Search by:'/>;
    }

    // Default search input
    const searchKey = 'q';
    const searchLabel = 'Search';
    const searchValue = readValue(searchKey) || '';
    const onSearchChange = (val) => {
        if (typeof setSearch === 'function') return setSearch(val);
        return writeValue(searchKey, val);
    };

    return (
        <FilterBar
            filters={filtersForBar}
            search={{ label: searchLabel, value: searchValue, onChange: onSearchChange }}
        />
    );
}
